import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

/**
 * Keeps the rulers in step with the scroller: the tick strips' scroll transforms, and the
 * rAF-coalesced scroll/viewport snapshot that drives ruler tick virtualization.
 */
export function useRulerScrollSync({
  scrollRef,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
}) {
  /** Ruler tick strips: transform updated in scroll listener; viewport sync is rAF-coalesced state for tick virtualization. */
  const horizontalRulerStripRef = useRef<HTMLDivElement | null>(null)
  const verticalRulerStripRef = useRef<HTMLDivElement | null>(null)

  /** Scroll + viewport for ruler tick virtualization (Phase 2). */
  const [rulerViewport, setRulerViewport] = useState({
    sl: 0,
    st: 0,
    vw: 0,
    vh: 0,
  })
  const rulerViewportRafRef = useRef<number | null>(null)

  const scheduleRulerViewportSync = useCallback(() => {
    if (rulerViewportRafRef.current != null) return
    rulerViewportRafRef.current = requestAnimationFrame(() => {
      rulerViewportRafRef.current = null
      const sc = scrollRef.current

      if (!sc) return
      setRulerViewport({
        sl: sc.scrollLeft,
        st: sc.scrollTop,
        vw: sc.clientWidth,
        vh: sc.clientHeight,
      })
    })
  }, [scrollRef])

  useEffect(() => {
    const onResize = () => scheduleRulerViewportSync()

    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [scheduleRulerViewportSync])

  useEffect(
    () => () => {
      if (rulerViewportRafRef.current != null) {
        cancelAnimationFrame(rulerViewportRafRef.current)
        rulerViewportRafRef.current = null
      }
    },
    []
  )

  const syncRulerScrollTransforms = useCallback(() => {
    const scroller = scrollRef.current

    if (!scroller) return

    const sl = scroller.scrollLeft
    const st = scroller.scrollTop

    horizontalRulerStripRef.current?.style.setProperty('transform', `translate3d(${-sl}px, 0, 0)`)
    verticalRulerStripRef.current?.style.setProperty('transform', `translate3d(0, ${-st}px, 0)`)
    scheduleRulerViewportSync()
  }, [scheduleRulerViewportSync, scrollRef])

  return {
    horizontalRulerStripRef,
    verticalRulerStripRef,
    rulerViewport,
    setRulerViewport,
    syncRulerScrollTransforms,
  }
}
